import { useEffect, useRef, useState } from 'react';
import { getSessionStorageItem } from './sessionStorage';

/**
 * Hook to restore session storage from authentication cookies when opening a new tab.
 * This solves the problem where users are already authenticated (cookies exist) but
 * sessionStorage is empty because it's tab-specific.
 *
 * Uses synchronous checking during render to prevent race conditions where components
 * check sessionStorage before restoration completes.
 *
 * @param cookieNames - Object containing the names of cookies to check
 * @param onRestore - Callback function to fetch and restore session data
 * @param enabled - Whether restoration should be enabled (default: true)
 * @returns isRestoring - boolean indicating if restoration is in progress
 */
export const useRestoreSessionFromAuth = (cookieNames, onRestore, enabled = true) => {
  const hasCheckedRef = useRef(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Synchronously check during render if restoration is needed
  // This prevents children from mounting before we know if restoration is needed
  if (typeof window !== 'undefined' && enabled && !hasCheckedRef.current && !isRestoring) {
    const inforUser = getSessionStorageItem(cookieNames.inforUser);
    const typeView = getSessionStorageItem(cookieNames.typeView);

    // If sessionStorage is empty, we need to restore
    if (!inforUser || !typeView) {
      hasCheckedRef.current = true;
      // Set flag synchronously - this will cause a re-render but children won't mount yet
      setIsRestoring(true);

      // Start restoration asynchronously
      onRestore().then(() => {
        setIsRestoring(false);
      }).catch(error => {
        console.error('[useRestoreSessionFromAuth] Restoration failed:', error);
        setIsRestoring(false);
      });
    } else {
      hasCheckedRef.current = true;
    }
  }

  // Handle disabled state
  useEffect(() => {
    if (!enabled && hasCheckedRef.current) {
      hasCheckedRef.current = false;
      setIsRestoring(false);
    }
  }, [enabled]);
  return isRestoring;
};